import { useState, useEffect } from 'react';
import { optimizeWikimedia } from '../utils/imageOptimizer';

// Module-level cache so repeated renders/cards don't re-fetch
const cache = new Map();

/**
 * Fetch a place thumbnail from the Wikipedia REST API.
 * Returns { src, loading, error }.
 */
export default function useWikipediaImage(city, country) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = city || country;
    if (!query) return;

    const key = query.toLowerCase().trim();

    // Return cached result immediately
    if (cache.has(key)) {
      const cached = cache.get(key);
      if (cached) setSrc(cached);
      else setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(key)}`)
      .then(res => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        const thumb = data.thumbnail?.source || data.originalimage?.source;
        if (thumb) {
          const optimized = optimizeWikimedia(thumb, { width: 600, height: 400 });
          cache.set(key, optimized);
          setSrc(optimized);
        } else {
          cache.set(key, null);
          setError(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        cache.set(key, null);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [city, country]);

  return { src, loading, error };
}
